/** Pixa — Genel Bakış: ciro/sipariş/sepet, kanal dağılımı, veriden çıkan somut içgörüler,
 *  açık bulgular, trend, en çok satanlar ve kategoriler.
 *
 *  Kanal dağılımı istemcide hesaplanıyor: Pixa'nın dashboardOverview raporu platformIds
 *  filtresini kabul ediyor gibi görünüp her kanal için aynı toplamı döndürüyor. Dönemin
 *  siparişleri (≤3×200) çekilip pazaryerine göre toplanıyor. */
import React, { useState, useEffect, useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import {
  API, T, FONT, Button, Card, Loader, ErrorBox, Thumb, axios, fmtN, money, timeAgo, tint, useData, useLang,
} from './shared';
import { goToFinding } from './focus';
import { useIsMobile } from '../poyais-ui/hooks';
import { MarketMark } from './marketplaces';
import { kindInfo } from './Findings';
import { addTranslations } from '../i18n';
import PoyaisSpinner from '../PoyaisSpinner';

addTranslations({
  'Ciro': 'Revenue', 'Sipariş': 'Orders', 'Ort. Sepet': 'Avg. Basket', 'önceki döneme göre': 'vs previous period',
  'Kanal Dağılımı': 'Channel Mix', 'Akıllı İçgörüler': 'Smart Insights', 'Açık Bulgular': 'Open Findings',
  'Tümünü Gör': 'View all', 'En Çok Satanlar': 'Top Sellers', 'Kategoriler': 'Categories', 'Günlük Trend': 'Daily Trend', 'Trend': 'Trend', 'Seçili Dönem': 'Selected Period', 'Bu Ay': 'This Month',
  'Geçen Ay': 'Last Month', 'Son 6 Ay': 'Last 6 Months', 'Son 1 Yıl': 'Last 12 Months',
  'AI ile Yorumla': 'Interpret with AI', 'AI düşünüyor…': 'AI is thinking…', 'adet': 'units', 'sipariş': 'orders',
});

const SEV_COLOR: Record<string, string> = { high: T.red, medium: T.amber, low: T.blue, info: T.textDim };

function useChannelSplit(clientId: number, start?: string, end?: string) {
  const [state, setState] = useState<{ rows: any[]; partial: boolean; loading: boolean }>({ rows: [], partial: false, loading: true });
  useEffect(() => {
    let alive = true;
    (async () => {
      setState(s => ({ ...s, loading: true }));
      const all: any[] = []; let partial = false;
      try {
        for (let page = 0; page < 3; page++) {
          const r = await axios.get(`${API}/pixa/${clientId}/orders`, { params: { limit: 200, offset: page * 200, start_date: start, end_date: end } });
          all.push(...(r.data?.rows || []));
          if (!r.data?.pagination?.hasMore) break;
          if (page === 2) partial = true;
        }
      } catch { /* kanal kırılımı opsiyonel */ }
      if (!alive) return;
      const by: Record<string, { name: string; revenue: number; orders: number }> = {};
      for (const o of all) {
        if ([7, 19, 10].includes(Number(o.status))) continue;   // iptal/başarısız ciroya sayılmaz
        const k = o.platform || '—';
        by[k] = by[k] || { name: k, revenue: 0, orders: 0 };
        by[k].revenue += Number(o.generalTotal || 0); by[k].orders += 1;
      }
      setState({ rows: Object.values(by).sort((a, b) => b.revenue - a.revenue), partial, loading: false });
    })();
    return () => { alive = false; };
  }, [clientId, start, end]);
  return state;
}

// Trend grafiği dönem seçici: 'page' = sayfanın tarih filtresi; diğerleri grafiğe özel.
type TrendRange = 'page' | 'this' | 'last' | '6m' | '12m';
const TREND_RANGES: [TrendRange, string][] = [['page', 'Seçili Dönem'], ['this', 'Bu Ay'], ['last', 'Geçen Ay'], ['6m', 'Son 6 Ay'], ['12m', 'Son 1 Yıl']];
const MONTHS_SHORT = ['Oca', 'Şub', 'Mar', 'Nis', 'May', 'Haz', 'Tem', 'Ağu', 'Eyl', 'Eki', 'Kas', 'Ara'];
const MONTHS_LONG = ['Ocak', 'Şubat', 'Mart', 'Nisan', 'Mayıs', 'Haziran', 'Temmuz', 'Ağustos', 'Eylül', 'Ekim', 'Kasım', 'Aralık'];
const isoDay = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
function trendDates(r: TrendRange): { start: string; end: string } | null {
  const now = new Date();
  if (r === 'this') return { start: isoDay(new Date(now.getFullYear(), now.getMonth(), 1)), end: isoDay(now) };
  if (r === 'last') return { start: isoDay(new Date(now.getFullYear(), now.getMonth() - 1, 1)), end: isoDay(new Date(now.getFullYear(), now.getMonth(), 0)) };
  if (r === '6m') return { start: isoDay(new Date(now.getFullYear(), now.getMonth() - 5, 1)), end: isoDay(now) };
  if (r === '12m') return { start: isoDay(new Date(now.getFullYear(), now.getMonth() - 11, 1)), end: isoDay(now) };
  return null;
}
const DAY_MS = 86400000;
const spanDays = (a?: string, b?: string) => (a && b ? Math.round((Date.parse(b) - Date.parse(a)) / DAY_MS) + 1 : 0);

export function OverviewPage({ clientId, start, end }: { clientId: number; start?: string; end?: string }) {
  const { t } = useLang();
  const isMobile = useIsMobile();
  const [metric, setMetric] = useState<'revenue' | 'orderCount'>('revenue');
  const [trendRange, setTrendRange] = useState<TrendRange>('page');
  const [anom, setAnom] = useState<any>(null);
  const [ai, setAi] = useState<{ busy: boolean; text: string | null; err: string | null }>({ busy: false, text: null, err: null });

  const { data, loading, error } = useData(clientId, '/overview', { start_date: start, end_date: end });
  const split = useChannelSplit(clientId, start, end);
  // Grafiğe özel dönem seçiliyse yalnızca trend için ayrı çekilir (üstteki kartlar sayfa filtresinde kalır)
  const own = trendDates(trendRange);
  const ownData = useData(clientId, '/overview', { start_date: own?.start, end_date: own?.end }, !own);

  useEffect(() => {
    axios.get(`${API}/pixa/${clientId}/anomalies`, { params: { limit: 6 } }).then(r => setAnom(r.data)).catch(() => setAnom(null));
  }, [clientId]);

  const m = data?.metrics || {};
  const ch = data?.metricChanges || {};
  const cur = data?.currency || 'TRY';
  const charts = data?.charts || {};
  // 2 aydan uzun aralıklar aylık toplanır (günlük çizgi 100+ noktada okunmaz); kısa aralıklar günlük kalır
  const trendRangeStart = own ? own.start : start, trendRangeEnd = own ? own.end : end;
  const trendSrc = own ? (ownData.data?.charts?.revenueTrend || []) : (charts.revenueTrend || []);
  const monthly = spanDays(trendRangeStart, trendRangeEnd) > 62;
  const trend = useMemo(() => {
    const days = trendSrc.map((d: any) => ({ period: String(d.period), revenue: Number(d.revenue || 0), orderCount: Number(d.orderCount || 0) }));
    if (!monthly) return days;
    const by: Record<string, { period: string; revenue: number; orderCount: number }> = {};
    for (const d of days) {
      const k = d.period.slice(0, 7);
      by[k] = by[k] || { period: k, revenue: 0, orderCount: 0 };
      by[k].revenue += d.revenue; by[k].orderCount += d.orderCount;
    }
    return Object.values(by).sort((a, b) => a.period.localeCompare(b.period));
  }, [trendSrc, monthly]);
  const monthLabel = (p: string, long = false) => { const [y, mo] = p.split('-'); return `${(long ? MONTHS_LONG : MONTHS_SHORT)[Number(mo) - 1]} ${y}`; };
  const topProducts = (charts.topProducts || []).slice(0, 8);
  const cats = (charts.categorySales || []).slice(0, 8);
  const catMax = Math.max(1, ...cats.map((c: any) => Number(c.revenue || 0)));

  const insights = useMemo(() => {
    const out: { tone: string; title: string; text: string }[] = [];
    const hours: any[] = charts.hourlyOrders?.items || [];
    const totalH = hours.reduce((s, h) => s + Number(h.orderCount || 0), 0);
    if (totalH >= 20) {
      let best = { start: 0, n: -1 };
      for (let i = 0; i < 24; i++) {
        const n = [0, 1, 2].reduce((s, k) => s + Number(hours.find(h => Number(h.hour) === (i + k) % 24)?.orderCount || 0), 0);
        if (n > best.n) best = { start: i, n };
      }
      const pct = Math.round(best.n / totalH * 100);
      const pad = (x: number) => String(x % 24).padStart(2, '0');
      out.push({ tone: 'blue', title: `Siparişlerin %${pct}'i ${pad(best.start)}:00–${pad(best.start + 3)}:00 arasında geliyor.`,
        text: 'Günün en yoğun 3 saati bu. Reklam bütçesini ve kampanya duyurularını bu saatlere ağırlıklandırın; müşteri sorularına da bu saatlerde hızlı cevap verin.' });
    }
    const days: any[] = charts.weekdayOrders?.items || [];
    const totalD = days.reduce((s, d) => s + Number(d.orderCount || 0), 0);
    if (totalD >= 20) {
      const sorted = [...days].sort((a, b) => Number(b.orderCount) - Number(a.orderCount));
      const hi = sorted[0], lo = sorted[sorted.length - 1];
      if (hi && lo && Number(hi.orderCount) > Number(lo.orderCount) * 1.5) {
        out.push({ tone: 'blue', title: `En güçlü gün ${hi.label}, en zayıf gün ${lo.label}.`,
          text: `${hi.label} günü ${fmtN(hi.orderCount)}, ${lo.label} günü ${fmtN(lo.orderCount)} sipariş geldi. Zayıf güne özel indirim ya da kupon ile talebi dengelemeyi deneyin.` });
      }
    }
    const total = split.rows.reduce((s, r) => s + r.revenue, 0);
    if (total > 0 && split.rows.length > 1) {
      const top = split.rows[0]; const share = Math.round(top.revenue / total * 100);
      if (share >= 60) out.push({ tone: 'amber', title: `Cironun %${share}'i tek kanaldan geliyor: ${top.name}.`,
        text: 'Tek kanala bağımlılık risk yaratır (komisyon artışı, hesap kısıtlaması). Diğer kanallarda listelenmeyen ya da kapalı ürünleri Asistan > Bulgular\'dan kontrol edin.' });
    }
    const dO = Number(ch.orderCount), dB = Number(ch.avgBasket);
    if (isFinite(dO) && isFinite(dB) && Math.abs(dO) >= 3 && Math.abs(dB) >= 3 && Math.sign(dO) !== Math.sign(dB)) {
      out.push({ tone: dB < 0 ? 'amber' : 'green', title: dB < 0 ? 'Daha çok ama daha küçük sepetler.' : 'Daha az ama daha büyük sepetler.',
        text: `Sipariş sayısı %${Math.abs(dO)} ${dO > 0 ? 'arttı' : 'azaldı'}, ortalama sepet %${Math.abs(dB)} ${dB > 0 ? 'büyüdü' : 'küçüldü'}. ${dB < 0 ? 'Sepeti büyütmek için ücretsiz kargo eşiği ya da ikinci üründe indirim deneyin.' : 'Yüksek sepetli müşterileri tekrar satın almaya yönlendirin.'}` });
    }
    if (m.hasCostData === false || m.hasCostData === 'False') {
      out.push({ tone: 'amber', title: 'Kârlılık hesaplanamıyor.',
        text: 'Pixa\'da ürün maliyetleri girilmemiş. Maliyetler girilince brüt kâr, marj ve "maliyetin altında satış" uyarıları otomatik çalışır.' });
    }
    return out;
  }, [charts, split.rows, ch, m]);

  const interpret = async () => {
    setAi({ busy: true, text: null, err: null });
    try {
      const r = await axios.post(`${API}/pixa/${clientId}/ask`, {
        question: 'Bu dönemin performansını 4-5 maddede yorumla: ne iyi gidiyor, ne kötü gidiyor, nedeni ne olabilir ve bu hafta ne yapmalıyım. Rakam ve kanal adı vererek somut ol.', history: [],
      });
      setAi({ busy: false, text: r.data?.answer || '—', err: null });
    } catch (e: any) { setAi({ busy: false, text: null, err: e?.response?.data?.detail || t('Hata') }); }
  };

  if (loading) return <Loader />;
  if (error) return <ErrorBox msg={error} />;

  const changeColor = (v: any) => Number(v) > 0 ? T.greenText : Number(v) < 0 ? T.redText : T.textDim;
  const openFindings = (anom?.rows || []).filter((r: any) => !r.resolved_at);
  const totalOpen = anom?.total_open ?? openFindings.length;
  const splitTotal = split.rows.reduce((s, r) => s + r.revenue, 0);
  const TONE: Record<string, string> = { blue: T.blue, amber: T.amber, green: T.green };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14, fontFamily: FONT }}>
      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : 'repeat(3, minmax(0,1fr))', gap: 10 }}>
        {[
          { label: t('Ciro'), value: money(m.totalRevenue, cur), change: ch.totalRevenue },
          { label: t('Sipariş'), value: fmtN(m.orderCount), change: ch.orderCount },
          { label: t('Ort. Sepet'), value: money(m.avgBasket, cur), change: ch.avgBasket },
        ].map((k, i) => (
          <Card key={i} pad={18}>
            <div style={{ fontSize: 11.5, fontWeight: 700, color: T.textDim }}>{k.label}</div>
            <div style={{ fontSize: isMobile ? 20 : 26, fontWeight: 800, color: T.text, letterSpacing: '-0.02em', marginTop: 6 }}>{k.value}</div>
            {k.change !== null && k.change !== undefined && (
              <div style={{ fontSize: 11.5, fontWeight: 700, color: changeColor(k.change), marginTop: 4 }}>
                {Number(k.change) > 0 ? '↑' : Number(k.change) < 0 ? '↓' : '·'} %{Math.abs(Number(k.change))} <span style={{ color: T.textDim, fontWeight: 500 }}>{t('önceki döneme göre')}</span>
              </div>
            )}
          </Card>
        ))}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0,1fr) minmax(0,1.2fr)', gap: 14 }}>
        <Card pad={18}>
          <div style={{ fontSize: 13.5, fontWeight: 800, color: T.text, marginBottom: 12 }}>{t('Kanal Dağılımı')}</div>
          {split.loading ? <PoyaisSpinner size={26} pad={10}/> : split.rows.length === 0 ? <div style={{ fontSize: 12, color: T.textDim }}>—</div> : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {split.rows.map(r => {
                const pct = splitTotal ? r.revenue / splitTotal * 100 : 0;
                return (
                  <div key={r.name}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5, marginBottom: 5 }}>
                      <MarketMark name={r.name} size={20} /><b style={{ color: T.text, flex: 1 }}>{r.name}</b>
                      <span style={{ color: T.textDim }}>%{Math.round(pct)} · {fmtN(r.orders)} {t('sipariş')}</span>
                      <b style={{ color: T.text, minWidth: 90, textAlign: 'right' as const }}>{money(r.revenue, cur)}</b>
                    </div>
                    <div style={{ height: 6, borderRadius: 999, background: 'var(--py-track-bg)', overflow: 'hidden' }}>
                      <div style={{ width: `${pct}%`, height: '100%', background: T.accent, borderRadius: 999 }} />
                    </div>
                  </div>
                );
              })}
              {split.partial && <div style={{ fontSize: 10.5, color: T.textFaint }}>İlk 600 sipariş üzerinden hesaplandı.</div>}
            </div>
          )}
        </Card>

        <Card pad={0}>
          <div style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 10, borderBottom: `1px solid ${T.border}` }}>
            <b style={{ fontSize: 13.5, color: T.text }}>{t('Akıllı İçgörüler')}</b>
            <Button variant="soft" color={T.accent} onClick={interpret} disabled={ai.busy} style={{ marginLeft: 'auto', padding: '6px 12px', fontSize: 11.5 }}>
              {ai.busy ? t('AI düşünüyor…') : t('AI ile Yorumla')}
            </Button>
          </div>
          <div style={{ padding: '6px 18px 14px', display: 'flex', flexDirection: 'column' }}>
            {insights.length === 0 && !ai.text && <div style={{ fontSize: 12, color: T.textDim, padding: '10px 0' }}>—</div>}
            {insights.map((x, i) => (
              <div key={i} style={{ display: 'flex', gap: 10, padding: '10px 0', borderTop: i ? `1px solid ${T.border}` : 'none' }}>
                <span style={{ width: 8, height: 8, borderRadius: 999, background: TONE[x.tone], marginTop: 6, flexShrink: 0 }} />
                <div>
                  <div style={{ fontSize: 12.5, fontWeight: 800, color: T.text }}>{x.title}</div>
                  <div style={{ fontSize: 12, color: T.textMid, lineHeight: 1.55, marginTop: 2 }}>{x.text}</div>
                </div>
              </div>
            ))}
            {(ai.text || ai.err) && (
              <div style={{ marginTop: 8, padding: '12px 14px', borderRadius: 12, background: tint(T.accent, 7), border: `1px solid ${tint(T.accent, 35)}`,
                fontSize: 12.5, color: ai.err ? T.redText : T.text, lineHeight: 1.65, whiteSpace: 'pre-wrap' as const }}>{ai.err || ai.text}</div>
            )}
          </div>
        </Card>
      </div>

      {totalOpen > 0 && (
        <Card pad={0}>
          <div style={{ padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 10, borderBottom: `1px solid ${T.border}`, flexWrap: 'wrap' as const }}>
            <b style={{ fontSize: 13.5, color: T.text }}>{t('Açık Bulgular')} <span style={{ color: T.textDim, fontWeight: 600 }}>({fmtN(totalOpen)})</span></b>
            {anom?.last_scan && <span style={{ fontSize: 11, color: T.textDim }}>{t('Son tarama')}: {timeAgo(anom.last_scan.finished_at || anom.last_scan.started_at)}</span>}
            <Button variant="ghost" color={T.accent} onClick={() => window.dispatchEvent(new CustomEvent('poyais-nav', { detail: 'pixa-asistan' }))}
              style={{ marginLeft: 'auto', padding: '6px 12px', fontSize: 11.5 }}>{t('Tümünü Gör')} →</Button>
          </div>
          <div style={{ padding: '4px 8px 8px' }}>
            {openFindings.slice(0, 6).map((g: any) => {
              const scope = (g.scope || '').split('·')[0].trim();
              const channel = /eşleşmeyen|sipariş|aktarım/i.test(scope) ? (scope.split(/\s+/)[0] || '') : scope;
              return (
                <button key={g.id} onClick={() => { if (!goToFinding(g.kind, g.scope)) window.dispatchEvent(new CustomEvent('poyais-nav', { detail: 'pixa-asistan' })); }}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '9px 10px', border: 'none', background: 'none', cursor: 'pointer', fontFamily: 'inherit', borderRadius: 8, textAlign: 'left' as const }}>
                  <span style={{ width: 4, height: 22, borderRadius: 3, background: SEV_COLOR[g.severity] || T.textDim, flexShrink: 0 }} />
                  {channel ? <MarketMark name={channel} size={20} /> : <span style={{ width: 20 }} />}
                  <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, color: T.text, fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>
                    {t(kindInfo(g.kind).title)}{channel ? <span style={{ color: T.textDim, fontWeight: 500 }}> · {channel}</span> : null}
                  </span>
                  <b style={{ fontSize: 12.5, color: T.text }}>{fmtN(g.count)}</b>
                  <span style={{ color: T.textDim }}>›</span>
                </button>
              );
            })}
          </div>
        </Card>
      )}

      {(trend.length > 0 || own) && (
        <Card pad={20}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16, flexWrap: 'wrap' as const, gap: 10 }}>
            <div style={{ fontSize: 13.5, fontWeight: 800, color: T.text }}>{t('Trend')}</div>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' as const }}>
              <select value={trendRange} onChange={e => setTrendRange(e.target.value as TrendRange)} aria-label={t('Trend')}
                style={{ padding: '6px 9px', borderRadius: 9, border: `1px solid ${T.borderMid}`, background: T.surface, fontSize: 11, fontWeight: 700,
                  color: T.text, fontFamily: 'inherit', cursor: 'pointer', flexShrink: 0 }}>
                {TREND_RANGES.map(([k, label]) => <option key={k} value={k}>{t(label)}</option>)}
              </select>
              {(['revenue', 'orderCount'] as const).map(k => (
                <Button key={k} variant={metric === k ? 'soft' : 'ghost'} color={T.accent} onClick={() => setMetric(k)}
                  style={{ padding: '4px 12px', fontSize: 11, fontWeight: metric === k ? 700 : 500 }}>{k === 'revenue' ? t('Ciro') : t('Sipariş')}</Button>
              ))}
            </div>
          </div>
          {own && ownData.loading ? <Loader /> : trend.length === 0 ? (
            <div style={{ padding: '40px 0', textAlign: 'center' as const, fontSize: 12.5, color: T.textDim }}>{t('Bu dönem için veri yok.')}</div>
          ) : (
          <ResponsiveContainer width="100%" height={220}>
            <LineChart data={trend} margin={{ top: 4, right: 4, left: monthly ? 4 : -20, bottom: 0 }}>
              <XAxis dataKey="period" tick={{ fontSize: 10, fill: T.textDim }} tickFormatter={(v: string) => monthly ? monthLabel(v) : (v || '').slice(5)} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 10, fill: T.textDim }} axisLine={false} tickLine={false} width={monthly ? 44 : undefined}
                tickFormatter={monthly ? (v: number) => (v >= 1000 ? `${Math.round(v / 1000)}K` : String(v)) : undefined} />
              <Tooltip formatter={(v: any) => metric === 'revenue' ? money(v, cur) : fmtN(v)} labelFormatter={l => monthly ? monthLabel(String(l), true) : `${t('Tarih')}: ${l}`} />
              <Line type="monotone" dataKey={metric} stroke={T.accent} strokeWidth={2} dot={monthly ? { r: 3 } : false} isAnimationActive={false} />
            </LineChart>
          </ResponsiveContainer>
          )}
        </Card>
      )}

      {(topProducts.length > 0 || cats.length > 0) && (
        <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'minmax(0,1.2fr) minmax(0,1fr)', gap: 14 }}>
          {topProducts.length > 0 && (
            <Card pad={18}>
              <div style={{ fontSize: 13.5, fontWeight: 800, color: T.text, marginBottom: 10 }}>{t('En Çok Satanlar')}</div>
              {topProducts.map((p: any, i: number) => (
                <div key={p.productId || i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 0', borderTop: i ? `1px solid ${T.border}` : 'none' }}>
                  <span style={{ width: 18, fontSize: 11, fontWeight: 800, color: T.textDim }}>{i + 1}</span>
                  <Thumb url={p.thumbUrl} size={34} />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 12.5, fontWeight: 600, color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>{p.productName}</div>
                    <div style={{ fontSize: 10.5, color: T.textDim }}>{[p.sku, p.quantity !== undefined ? `${fmtN(p.quantity)} ${t('adet')}` : null, p.percent ? `%${p.percent}` : null].filter(Boolean).join(' · ')}</div>
                  </div>
                  <b style={{ fontSize: 12.5, color: T.text }}>{money(p.revenue, cur)}</b>
                </div>
              ))}
            </Card>
          )}
          {cats.length > 0 && (
            <Card pad={18}>
              <div style={{ fontSize: 13.5, fontWeight: 800, color: T.text, marginBottom: 10 }}>{t('Kategoriler')}</div>
              {cats.map((c: any, i: number) => (
                <div key={i} style={{ padding: '6px 0' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10, fontSize: 12, marginBottom: 4 }}>
                    <span style={{ color: T.text, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }} title={c.categoryName}>
                      {String(c.categoryName || '—').split('/').slice(-2).join(' / ').trim()} <span style={{ color: T.textDim }}>· {fmtN(c.quantity)} {t('adet')}</span>
                    </span>
                    <b style={{ color: T.text, flexShrink: 0 }}>{money(c.revenue, cur)}</b>
                  </div>
                  <div style={{ height: 5, borderRadius: 999, background: 'var(--py-track-bg)', overflow: 'hidden' }}>
                    <div style={{ width: `${Number(c.revenue || 0) / catMax * 100}%`, height: '100%', background: T.blue, borderRadius: 999 }} />
                  </div>
                </div>
              ))}
            </Card>
          )}
        </div>
      )}
    </div>
  );
}
